import { useEffect, useRef } from "react";
import { useGameStore } from "@/store/gameStore";
import { decideAction, decideRescueAction, decidePickupAction } from "@/ai/AIPlayer";
import { enumerateLegalActions } from "@/engine/LegalActions";
import type { GameState } from "@/types/game";

// AI 每步执行间隔（毫秒）：放慢节奏便于玩家观察与调试
const AI_DELAY = 1500;

export function useGameDriver() {
  const state = useGameStore((s) => s.state);
  const dispatch = useGameStore((s) => s.dispatch);
  const humanId = useGameStore((s) => s.humanPlayerId);
  const rescueDismissed = useGameStore((s) => s.rescueDismissed);
  const autoSkipEmptyResponse = useGameStore((s) => s.autoSkipEmptyResponse);
  const lastStateRef = useRef<GameState | null>(null);

  useEffect(() => {
    if (!state || state.phase === "game-over") return;

    // Strict Mode 双挂载守卫：同一 state 不重复调度
    if (state === lastStateRef.current) return;
    lastStateRef.current = state;

    const s = state;

    // 1) 响应窗口
    if (s.phase === "response" && s.responseWindow) {
      const w = s.responseWindow;
      const pending = w.responders.find(
        (rid) => w.decisions[rid] === "pending"
      );
      if (!pending) return;

      // 自动跳过模式：所有 pending 响应者都无可响应牌时立即 pass
      // 注意：会泄露"无牌"信息（其他玩家从无响应窗口推断持有者无闪避等），默认关闭
      if (autoSkipEmptyResponse) {
        const anyCanRespond = w.responders.some((rid) => {
          if (w.decisions[rid] !== "pending") return false;
          return enumerateLegalActions(s, rid).some(
            (a) => a.type === "respond"
          );
        });
        if (!anyCanRespond) {
          const id = setTimeout(() => {
            dispatch({ type: "pass-response", playerId: pending });
          }, 0);
          return () => clearTimeout(id);
        }
      }

      if (pending === humanId) {
        // 人类无可响应牌 → 自动跳过，不弹窗
        const humanActions = enumerateLegalActions(s, humanId);
        const hasRespond = humanActions.some((a) => a.type === "respond");
        if (!hasRespond) {
          const id = setTimeout(() => {
            dispatch({ type: "pass-response", playerId: humanId });
          }, AI_DELAY);
          return () => clearTimeout(id);
        }
        return; // 有可响应牌 → 等人类 UI
      }

      const id = setTimeout(() => {
        dispatch(decideAction(s, pending));
      }, AI_DELAY);
      return () => clearTimeout(id);
    }

    // 2) 拾遗窗口（被动弃牌/判定牌后）：人类等 UI（3 秒倒计时自动放弃），AI 立即决策
    if (s.phase === "pickup-response" && s.pickupWindow) {
      const w = s.pickupWindow;
      const pending = w.responders.find((rid) => w.decisions[rid] === "pending");
      if (!pending) return;
      if (pending === humanId) return; // PickupPrompt 处理显示与超时
      const id = setTimeout(() => {
        dispatch(decidePickupAction(s, pending));
      }, AI_DELAY);
      return () => clearTimeout(id);
    }

    // 3) play 阶段：先检查濒死救援
    if (s.phase === "play") {
      // 人类有救援候选 → 等 UI
      const humanRescues = enumerateLegalActions(s, humanId, {
        rescueOnlyForOthers: true,
      });
      if (humanRescues.length > 0 && !rescueDismissed) return;

      // AI 救援候选
      const rescue = decideRescueAction(s);
      if (rescue) {
        const id = setTimeout(() => dispatch(rescue), AI_DELAY);
        return () => clearTimeout(id);
      }
    }

    // 4) draw / play / discard 阶段
    const cur = s.players[s.currentPlayerIdx];
    if (cur.id === humanId) return; // 等人类 UI

    const id = setTimeout(() => {
      dispatch(decideAction(s, cur.id));
    }, AI_DELAY);
    return () => clearTimeout(id);
  }, [state, state?.phase, state?.currentPlayerIdx, state?.responseWindow, state?.pickupWindow, humanId, dispatch, rescueDismissed, autoSkipEmptyResponse]);
}
